import { useState } from 'react';

import { useAuth } from 'context/AuthProvider/AuthProvider';
import { useSnackbar } from 'context/SnackbarProvider/SnackbarProvider';
import { handleFetchResponse } from 'hooks/handleFetchResponse';
import { getAuthHeader } from 'utils/auth/getAuthHeader';
import { fetchData, StandardHTTPMethods } from 'utils/fetchData/fetchData';

const useFetchData = () => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const { setSnackbarInfo } = useSnackbar();
  const { getAccessToken } = useAuth();

  const getData = async <T>(
    url: string,
    handleData: (data: T) => void,
    handleBlob?: (blob: Blob, headers: Headers) => void
  ) => {
    const token = await getAccessToken();
    if (token) {
      setIsDataLoading(true);
      await handleFetchResponse(
        () =>
          fetchData<void, T>({
            method: 'GET',
            url: url,
            headers: getAuthHeader(token),
          }),
        setSnackbarInfo,
        'Failed to retrieve data',
        handleData,
        handleBlob
      );
      setIsDataLoading(false);
    }
  };

  const postData = async <B, R>(url: string, requestBody: B, handleData?: (data: R) => void) => {
    const token = await getAccessToken();
    if (token) {
      setIsDataLoading(true);
      await handleFetchResponse(
        () =>
          fetchData<B, R>({
            method: 'POST',
            url: url,
            headers: getAuthHeader(token),
            body: requestBody,
          }),
        setSnackbarInfo,
        'Failed to add data',
        handleData
      );
      setIsDataLoading(false);
    }
  };

  const patchData = async <B, R>(url: string, requestBody: B, handleData?: (data: R) => void) => {
    const token = await getAccessToken();
    if (token) {
      setIsDataLoading(true);
      await handleFetchResponse(
        () =>
          fetchData<B, R>({
            method: 'PATCH',
            url: url,
            headers: getAuthHeader(token),
            body: requestBody,
          }),
        setSnackbarInfo,
        'Failed to update data',
        handleData
      );
      setIsDataLoading(false);
    }
  };

  const deleteData = async <B, R>(url: string, handleData?: (data: R) => void) => {
    const token = await getAccessToken();
    if (token) {
      setIsDataLoading(true);
      await handleFetchResponse(
        () =>
          fetchData<B, R>({
            method: 'DELETE',
            url: url,
            headers: getAuthHeader(token),
          }),
        setSnackbarInfo,
        'Failed to delete data',
        handleData
      );
      setIsDataLoading(false);
    }
  };

  const uploadFile = async <T>(
    url: string,
    file: File,
    method: Extract<StandardHTTPMethods, 'POST' | 'PUT'> = 'PUT',
    handleData?: (data: T) => void
  ): Promise<boolean> => {
    const token = await getAccessToken();
    if (!token) return false;

    let uploadedSuccessfully = false;

    setIsDataLoading(true);
    await handleFetchResponse(
      async () => {
        const res = await fetchData<void, T>({
          method: method,
          url: url,
          headers: getAuthHeader(token),
          file: file,
        });
        if (!res.errorResponse) uploadedSuccessfully = true;
        return res;
      },
      setSnackbarInfo,
      'Failed to upload file',
      handleData
    );
    setIsDataLoading(false);
    return uploadedSuccessfully;
  };

  return { getData, postData, patchData, deleteData, uploadFile, isDataLoading };
};

export default useFetchData;
